import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { timeAgo } from '../lib/timeAgo';
import { useToast } from '../lib/useToast';
import { ConfirmModal } from '../components/ConfirmModal';
import { PageHeader } from '../ui/PageHeader';
import { SkeletonRows } from '../ui/Skeleton';
import { EmptyState } from '../ui/EmptyState';
import { ErrorBanner } from '../ui/ErrorBanner';
import { Field } from '../ui/Field';

const CONFIG_KEY = 'min_app_version';
const DIST_DAYS = 30;

type MinVersionValue = { ios: string | null; enforced: boolean; blocked: string[] };

type ConfigRow = { key: string; value: unknown; updated_at: string | null; updated_by: string | null };

type SessionRow = { user_id: string | null; app_version: string | null };

type VersionBucket = { version: string; users: number; sessions: number; parsed: number[] | null };

/** "1.1.8" -> [1,1,8]; null for junk (Expo Go SDK strings etc. still parse and read as high). */
function parseVersion(v: string): number[] | null {
  const parts = v.trim().split('.');
  if (parts.length < 2 || parts.length > 4) return null;
  const nums = parts.map((p) => (/^\d+$/.test(p) ? Number(p) : NaN));
  return nums.some((n) => !Number.isFinite(n)) ? null : nums;
}

/** semver-ish compare: a < b => -1 */
function compareVersions(a: number[], b: number[]): number {
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const x = a[i] ?? 0;
    const y = b[i] ?? 0;
    if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

function coerceBlockedList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  for (const v of value) {
    if (typeof v === 'string' && v.trim()) seen.add(v.trim());
  }
  return Array.from(seen);
}

function coerceMinVersion(value: unknown): MinVersionValue {
  if (value && typeof value === 'object') {
    const v = value as { ios?: unknown; enforced?: unknown; blocked?: unknown };
    return {
      ios: typeof v.ios === 'string' && v.ios.trim() ? v.ios.trim() : null,
      enforced: v.enforced === true,
      blocked: coerceBlockedList(v.blocked),
    };
  }
  return { ios: null, enforced: false, blocked: [] };
}

export function ForceUpdateScreen() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stored, setStored] = useState<MinVersionValue>({ ios: null, enforced: false, blocked: [] });
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [buckets, setBuckets] = useState<VersionBucket[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [distReadable, setDistReadable] = useState(true);

  // form state
  const [versionInput, setVersionInput] = useState('');
  const [enforcedInput, setEnforcedInput] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // per-version blocklist state
  const [blockTarget, setBlockTarget] = useState<VersionBucket | null>(null);
  const [blockBusyVersion, setBlockBusyVersion] = useState<string | null>(null);

  const { showError, showSuccess, toastNode } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcErr } = await supabase.rpc('admin_get_app_config');
      if (rpcErr) {
        if (rpcErr.message.includes('admin_get_app_config') || rpcErr.message.includes('42883')) {
          throw new Error(
            'Force-update config is not set up yet. Run supabase/sql/app_config_force_update.sql against production.',
          );
        }
        throw rpcErr;
      }
      const rows = (data ?? []) as ConfigRow[];
      const row = rows.find((r) => r.key === CONFIG_KEY);
      const value = coerceMinVersion(row?.value);
      setStored(value);
      setUpdatedAt(row?.updated_at ?? null);
      setVersionInput(value.ios ?? '');
      setEnforcedInput(value.enforced);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load force-update config');
    } finally {
      setLoading(false);
    }
  }, []);

  // Version distribution over the last 30 days (small table; fail-soft if RLS blocks).
  const loadDistribution = useCallback(async () => {
    try {
      const since = new Date(Date.now() - DIST_DAYS * 86400000).toISOString();
      const { data, error: qErr } = await supabase
        .from('app_sessions')
        .select('user_id, app_version')
        .gte('started_at', since)
        .limit(10000);
      if (qErr) {
        setDistReadable(false);
        return;
      }
      const rows = (data ?? []) as SessionRow[];
      const byVersion = new Map<string, { users: Set<string>; sessions: number }>();
      const allUsers = new Set<string>();
      for (const r of rows) {
        const v = r.app_version?.trim() || 'unknown';
        if (!byVersion.has(v)) byVersion.set(v, { users: new Set(), sessions: 0 });
        const b = byVersion.get(v)!;
        b.sessions += 1;
        if (r.user_id) {
          b.users.add(r.user_id);
          allUsers.add(r.user_id);
        }
      }
      const list: VersionBucket[] = Array.from(byVersion.entries())
        .map(([version, b]) => ({ version, users: b.users.size, sessions: b.sessions, parsed: parseVersion(version) }))
        .sort((a, b) => b.users - a.users || b.sessions - a.sessions);
      setBuckets(list);
      setTotalUsers(allUsers.size);
      setDistReadable(true);
    } catch {
      setDistReadable(false);
    }
  }, []);

  useEffect(() => {
    void load();
    void loadDistribution();
  }, [load, loadDistribution]);

  const inputParsed = useMemo(() => (versionInput.trim() ? parseVersion(versionInput) : null), [versionInput]);
  const inputInvalid = versionInput.trim() !== '' && !inputParsed;

  // Preview: who falls below the entered minimum. Unparseable versions are never counted as blocked.
  const previewBlocked = useMemo(() => {
    if (!inputParsed) return { users: 0, versions: 0 };
    let users = 0;
    let versions = 0;
    for (const b of buckets) {
      if (b.parsed && compareVersions(b.parsed, inputParsed) < 0) {
        users += b.users;
        versions += 1;
      }
    }
    return { users, versions };
  }, [buckets, inputParsed]);

  const dirty = versionInput.trim() !== (stored.ios ?? '') || enforcedInput !== stored.enforced;
  const canSave = dirty && !inputInvalid && !(enforcedInput && !versionInput.trim());

  // Highest version seen in the 30d distribution — never blockable (would wall everyone, including
  // users who are fully up to date).
  const highestVersion = useMemo(() => {
    let best: VersionBucket | null = null;
    for (const b of buckets) {
      if (!b.parsed) continue;
      if (!best || !best.parsed || compareVersions(b.parsed, best.parsed) > 0) best = b;
    }
    return best;
  }, [buckets]);

  // Rows for the per-version blocking table: every version in the 30d distribution, plus any
  // version still sitting in `blocked` that has since aged out of the distribution (count 0),
  // so a stale block can always be found and cleared.
  const blockRows = useMemo(() => {
    const byVersion = new Map<string, VersionBucket>();
    for (const b of buckets) byVersion.set(b.version, b);
    for (const v of stored.blocked) {
      if (!byVersion.has(v)) byVersion.set(v, { version: v, users: 0, sessions: 0, parsed: parseVersion(v) });
    }
    return Array.from(byVersion.values()).sort((a, b) => {
      if (a.parsed && b.parsed) {
        const c = compareVersions(b.parsed, a.parsed); // newest first
        if (c !== 0) return c;
      } else if (a.parsed && !b.parsed) {
        return -1;
      } else if (!a.parsed && b.parsed) {
        return 1;
      }
      return b.users - a.users;
    });
  }, [buckets, stored.blocked]);

  // Summary of what's actually walled right now, per the last-saved config (not pending form edits).
  const wallParts = useMemo(() => {
    const parts: string[] = [];
    if (stored.enforced && stored.ios) parts.push(`everything below ${stored.ios}`);
    if (stored.blocked.length > 0) parts.push(`[${stored.blocked.join(', ')}]`);
    return parts;
  }, [stored]);

  async function save() {
    setSaving(true);
    try {
      // Always write the full merged object — never drop the per-version blocklist when saving
      // the min-version/enforce form.
      const value: MinVersionValue = {
        ios: versionInput.trim() || null,
        enforced: enforcedInput,
        blocked: stored.blocked,
      };
      const { error: rpcErr } = await supabase.rpc('admin_set_app_config', {
        p_key: CONFIG_KEY,
        p_value: value,
      });
      if (rpcErr) throw rpcErr;
      setStored(value);
      setUpdatedAt(new Date().toISOString());
      showSuccess(value.enforced ? `Saved — builds below ${value.ios} are now walled` : 'Saved — enforcement is off');
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  }

  async function applyBlock(version: string) {
    setBlockBusyVersion(version);
    try {
      // Always write the full merged object — never drop ios/enforced when saving blocked toggles.
      const nextBlocked = Array.from(new Set([...stored.blocked, version]));
      const value: MinVersionValue = { ios: stored.ios, enforced: stored.enforced, blocked: nextBlocked };
      const { error: rpcErr } = await supabase.rpc('admin_set_app_config', {
        p_key: CONFIG_KEY,
        p_value: value,
      });
      if (rpcErr) throw rpcErr;
      setStored(value);
      setUpdatedAt(new Date().toISOString());
      showSuccess(`Blocked ${version} — those users now see the update wall.`);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to block version');
    } finally {
      setBlockBusyVersion(null);
      setBlockTarget(null);
    }
  }

  async function applyUnblock(version: string) {
    setBlockBusyVersion(version);
    try {
      const nextBlocked = stored.blocked.filter((v) => v !== version);
      const value: MinVersionValue = { ios: stored.ios, enforced: stored.enforced, blocked: nextBlocked };
      const { error: rpcErr } = await supabase.rpc('admin_set_app_config', {
        p_key: CONFIG_KEY,
        p_value: value,
      });
      if (rpcErr) throw rpcErr;
      setStored(value);
      setUpdatedAt(new Date().toISOString());
      showSuccess(`Unblocked ${version}.`);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to unblock version');
    } finally {
      setBlockBusyVersion(null);
    }
  }

  const maxUsers = buckets.length > 0 ? Math.max(...buckets.map((b) => b.users), 1) : 1;

  return (
    <div className="admin-page max-w-4xl mx-auto">
      <PageHeader
        title="Force Update"
        subtitle="Set the minimum app version, or block specific versions outright. Matching builds get a full-screen update wall immediately."
      />

      {error ? <ErrorBanner className="mb-6">{error}</ErrorBanner> : null}

      {loading ? (
        <SkeletonRows rows={6} className="py-4" />
      ) : (
        <div className="space-y-6">
          {/* Current state */}
          <section className="admin-card">
            <div className="admin-eyebrow mb-3">Current policy</div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <div className="flex items-baseline gap-2.5">
                <span className="text-[22px] font-semibold tabular-nums tracking-[-0.02em] text-textPrimary">
                  {stored.ios ?? '—'}
                </span>
                <span className="text-[12px] text-mutedDim">minimum iOS version</span>
              </div>
              <span className={`admin-status ${stored.enforced ? 'admin-status-warning' : 'admin-status-neutral'}`}>
                {stored.enforced ? 'Enforcement on' : 'Enforcement off'}
              </span>
              {updatedAt ? (
                <span className="text-[11px] tabular-nums text-mutedDim">updated {timeAgo(updatedAt)}</span>
              ) : null}
            </div>
            <p className="mt-3 max-w-xl text-[13px] leading-relaxed text-textPrimary">
              <span className="font-semibold">Walling:</span>{' '}
              {wallParts.length > 0 ? wallParts.join(' + ') : 'nothing — enforcement is off and no versions are blocked'}
            </p>
            <p className="mt-1.5 max-w-xl text-[12px] leading-relaxed text-mutedDim">
              This is live — the app-side gate reads this config and walls matching users immediately. Enforcement
              (below) and per-version blocks (further down) are two separate levers and they stack together.
            </p>
          </section>

          {/* Editor */}
          <section className="admin-card space-y-4">
            <div className="admin-eyebrow">Set policy</div>
            <div className="flex flex-wrap items-end gap-4">
              <Field label="Minimum iOS version" htmlFor="min-version">
                <input
                  id="min-version"
                  type="text"
                  value={versionInput}
                  onChange={(e) => setVersionInput(e.target.value)}
                  placeholder="e.g. 1.1.8"
                  className={`admin-input !w-40 ${inputInvalid ? '!border-danger' : ''}`}
                />
              </Field>
              <label className="flex cursor-pointer items-center gap-2.5 pb-1.5 text-[13px] text-textPrimary select-none">
                <input
                  type="checkbox"
                  checked={enforcedInput}
                  onChange={(e) => setEnforcedInput(e.target.checked)}
                  className="h-4 w-4 shrink-0 rounded border-border bg-canvas text-accent focus:ring-2 focus:ring-accent focus:ring-offset-0 focus:ring-offset-surface"
                />
                Enforce (block older builds)
              </label>
              <button
                type="button"
                disabled={!canSave || saving}
                onClick={() => setConfirmOpen(true)}
                className={`admin-btn ${enforcedInput ? 'admin-btn-danger' : 'admin-btn-primary'}`}
              >
                {saving ? 'Saving…' : 'Save policy'}
              </button>
            </div>
            {inputInvalid ? (
              <p className="text-[12px] text-danger">Version must look like 1.1.8 (2–4 numeric parts).</p>
            ) : null}
            {enforcedInput && !versionInput.trim() ? (
              <p className="text-[12px] text-warning">Set a version before turning enforcement on.</p>
            ) : null}
            {inputParsed ? (
              <p className="text-[12px] text-muted">
                Would block{' '}
                <span className="font-semibold tabular-nums text-textPrimary">
                  {previewBlocked.users.toLocaleString()}
                </span>{' '}
                of <span className="tabular-nums">{totalUsers.toLocaleString()}</span> active users ({DIST_DAYS}d) on{' '}
                <span className="tabular-nums">{previewBlocked.versions}</span> older version
                {previewBlocked.versions === 1 ? '' : 's'}
                {!enforcedInput ? ' — once you turn enforcement on' : ' — this is live now'}.
              </p>
            ) : null}
          </section>

          {/* Version distribution */}
          <section className="admin-card">
            <div className="mb-4 flex items-baseline justify-between gap-3">
              <div className="admin-eyebrow">Who is on what · last {DIST_DAYS} days</div>
              <span className="text-[11px] tabular-nums text-mutedDim">
                {totalUsers.toLocaleString()} active users
              </span>
            </div>
            {!distReadable ? (
              <EmptyState
                compact
                title="Version data unavailable"
                hint="app_sessions is not readable from this client — check the admin RLS policy."
              />
            ) : buckets.length === 0 ? (
              <EmptyState compact title="No sessions yet" hint={`No app sessions recorded in the last ${DIST_DAYS} days.`} />
            ) : (
              <div className="space-y-2">
                {buckets.map((b) => {
                  const belowMin = Boolean(inputParsed && b.parsed && compareVersions(b.parsed, inputParsed) < 0);
                  const pct = Math.max(4, Math.round((b.users / maxUsers) * 100));
                  return (
                    <div key={b.version} className="flex items-center gap-3">
                      <span
                        className={`w-16 shrink-0 text-right font-mono text-[12px] tabular-nums ${
                          belowMin ? 'text-danger' : 'text-textPrimary'
                        }`}
                      >
                        {b.version}
                      </span>
                      <div className="h-5 min-w-0 flex-1 rounded-[4px] bg-white/[0.03]">
                        <div
                          className={`h-full rounded-[4px] transition-all duration-300 ${
                            belowMin ? 'bg-danger/50' : 'bg-accent/70'
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="w-28 shrink-0 text-[11px] tabular-nums text-mutedDim">
                        {b.users.toLocaleString()} user{b.users === 1 ? '' : 's'} · {b.sessions.toLocaleString()} sess
                      </span>
                    </div>
                  );
                })}
                {inputParsed ? (
                  <p className="pt-2 text-[11px] text-mutedDim">
                    <span className="text-danger">Red</span> = below {versionInput.trim()} (would be blocked).
                    Unparseable versions are never blocked.
                  </p>
                ) : null}
              </div>
            )}
          </section>

          {/* Per-version blocking */}
          <section className="admin-card">
            <div className="mb-1 admin-eyebrow">Per-version blocking</div>
            <p className="mb-4 max-w-xl text-[12px] leading-relaxed text-mutedDim">
              Wall a specific version outright, independent of the minimum-version setting above. Explicit blocks
              stack with enforcement — a version can be walled even if it's above the minimum.
            </p>
            {!distReadable && stored.blocked.length === 0 ? (
              <EmptyState
                compact
                title="Version data unavailable"
                hint="app_sessions is not readable from this client — check the admin RLS policy."
              />
            ) : blockRows.length === 0 ? (
              <EmptyState compact title="No sessions yet" hint={`No app sessions recorded in the last ${DIST_DAYS} days.`} />
            ) : (
              <div className="space-y-2">
                {!distReadable ? (
                  <p className="text-[11px] text-warning">
                    Live distribution is unreadable right now — the "newest version" guardrail can't be verified, so
                    new blocks are disabled. Existing blocks below can still be cleared.
                  </p>
                ) : null}
                {blockRows.map((b) => {
                  const isBlocked = stored.blocked.includes(b.version);
                  const isHighest = distReadable && highestVersion?.version === b.version;
                  const busy = blockBusyVersion === b.version;
                  const blockDisabledReason = !distReadable
                    ? 'Version distribution unavailable — refresh before blocking a new version.'
                    : isHighest
                      ? 'Newest version — blocking it would wall everyone, including up-to-date users.'
                      : undefined;
                  return (
                    <div
                      key={b.version}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-[6px] border border-border/60 px-3 py-2"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="w-16 shrink-0 text-right font-mono text-[12px] tabular-nums text-textPrimary">
                          {b.version}
                        </span>
                        <span className="w-28 shrink-0 text-[11px] tabular-nums text-mutedDim">
                          {b.users.toLocaleString()} user{b.users === 1 ? '' : 's'} ({DIST_DAYS}d)
                        </span>
                        {isHighest ? <span className="admin-badge admin-badge-neutral">newest</span> : null}
                        {isBlocked ? <span className="admin-status admin-status-danger">Blocked</span> : null}
                      </div>
                      {isBlocked ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void applyUnblock(b.version)}
                          className="admin-btn admin-btn-sm admin-btn-ghost"
                        >
                          {busy ? 'Working…' : 'Unblock'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={busy || Boolean(blockDisabledReason)}
                          title={blockDisabledReason}
                          onClick={() => setBlockTarget(b)}
                          className="admin-btn admin-btn-sm admin-btn-secondary"
                        >
                          {busy ? 'Working…' : 'Block'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      )}

      <ConfirmModal
        open={confirmOpen}
        title={enforcedInput ? 'Enforce minimum version?' : 'Save force-update policy?'}
        message={
          enforcedInput
            ? `Builds below ${versionInput.trim()} will immediately get an update wall. Right now that affects ~${previewBlocked.users.toLocaleString()} of ${totalUsers.toLocaleString()} active users. Continue?`
            : `Save minimum version ${versionInput.trim() || '(none)'} with enforcement OFF. Nothing will be blocked by this setting.`
        }
        confirmLabel={enforcedInput ? 'Enforce' : 'Save'}
        danger={enforcedInput}
        busy={saving}
        onConfirm={() => void save()}
        onCancel={() => setConfirmOpen(false)}
      />

      <ConfirmModal
        open={Boolean(blockTarget)}
        title={`Block version ${blockTarget?.version ?? ''}?`}
        message={
          blockTarget
            ? `This immediately walls every user on ${blockTarget.version} (~${blockTarget.users.toLocaleString()} users) until they update from the App Store.`
            : ''
        }
        confirmLabel="Block version"
        danger
        busy={blockTarget !== null && blockBusyVersion === blockTarget.version}
        onConfirm={() => blockTarget && void applyBlock(blockTarget.version)}
        onCancel={() => setBlockTarget(null)}
      />
      {toastNode}
    </div>
  );
}
